import React, { useState, useRef } from 'react';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Building, Palette, Upload, CreditCard, FileText, Camera, Scale, Layout, Target, DollarSign, Info } from 'lucide-react';
import toast from 'react-hot-toast';
import RichTextEditor from './RichTextEditor';
import { Switch } from './ui/switch';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from './ui/tooltip';
import { LANDING_PAGE_TEMPLATES, getTemplateById, QUOTE_LAYOUTS, getLayoutId, getLayoutById } from '../data/landingPageTemplates';

const CompanySettings = ({ companyInfo, setCompanyInfo, onSave, saveButtonText, loading, onCompanyUpdate }) => {
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  // Use ref to store logo URL immediately after upload (solves React async state issue)
  const uploadedLogoUrlRef = useRef(null);

  const handleInputChange = (field, value) => {
    const updated = {
      ...companyInfo,
      [field]: value
    };
    console.log(`🔍 Field changed: ${field} = ${value}`);
    setCompanyInfo(updated);
    
    // Auch Parent informieren
    if (onCompanyUpdate) {
      onCompanyUpdate(updated);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('⚠️ Ungültiger Dateityp - Bitte wählen Sie eine Bilddatei (PNG, JPG, etc.)');
      return;
    }

    // Validate file size (2MB) - Show warning
    if (file.size > 2 * 1024 * 1024) {
      toast.error(`⚠️ Datei zu groß - Die Datei (${(file.size / 1024 / 1024).toFixed(1)}MB) ist größer als 2MB.`);
      return;
    }

    setLogoFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setLogoPreview(e.target.result);
    };
    reader.readAsDataURL(file);

    // Upload immediately
    setUploading(true);
    try {
      console.log('🔍 Uploading logo file immediately...', file.name, file.size);
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      const formData = new FormData();
      formData.append('logo', file);
      formData.append('company_id', companyInfo.id || 'default_company');
      
      const logoToken = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const uploadResponse = await fetch(`${BACKEND_URL}/api/companies/upload-logo`, {
        method: 'POST',
        headers: logoToken ? { 'Authorization': `Bearer ${logoToken}` } : {},
        body: formData
      });
      
      console.log('🔍 Logo upload response status:', uploadResponse.status);
      
      if (uploadResponse.ok) {
        const uploadResult = await uploadResponse.json();
        console.log('🔍 Logo upload result:', uploadResult);
        
        // Store logo URL in ref immediately (solves async state issue)
        uploadedLogoUrlRef.current = uploadResult.logoUrl;
        
        // Update company info with new logo URL
        setCompanyInfo(prev => ({
          ...prev,
          logo: uploadResult.logoUrl
        }));
        
        toast.success(`✅ Logo hochgeladen - ${uploadResult.logoFilename} (${(uploadResult.fileSize / 1024).toFixed(0)}KB)`, {
          duration: 4000
        });
      } else {
        const error = await uploadResponse.text();
        console.error('🔍 Logo upload error:', error);
        throw new Error('Logo-Upload fehlgeschlagen: ' + error);
      }
    } catch (error) {
      console.error('🔍 Logo upload error:', error);
      toast.error(`❌ Logo-Upload fehlgeschlagen - ${error.message}`);
      setLogoFile(null);
      setLogoPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const handleSaveSettings = async () => {
    setUploading(true);
    try {
      // Wenn Logo hochgeladen wurde, füge es zum companyInfo hinzu
      let finalCompanyInfo = { ...companyInfo };
      
      if (uploadedLogoUrlRef.current) {
        finalCompanyInfo.logo = uploadedLogoUrlRef.current;
      }
      
      console.log('🔍 Saving company settings:', finalCompanyInfo);
      
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');

      const response = await fetch(`${BACKEND_URL}/api/companies/${finalCompanyInfo.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(finalCompanyInfo)
      });
      
      if (response.ok) {
        const updatedCompany = await response.json();
        console.log('✅ Company saved:', updatedCompany);
        setCompanyInfo(updatedCompany);
        setSaveSuccess(true);
        
        toast.success('✅ Einstellungen erfolgreich gespeichert!', {
          duration: 3000
        });
        
        setTimeout(() => setSaveSuccess(false), 3000);
        
        // Auch Parent informieren
        if (onSave) {
          onSave(updatedCompany);
        }
      } else {
        const errorText = await response.text();
        console.error('❌ Save error:', errorText);
        throw new Error('Speichern fehlgeschlagen');
      }
      
    } catch (error) {
      console.error('🔍 Save error:', error);
      toast.error(`❌ Fehler beim Speichern - ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <Building className="w-6 h-6 text-blue-600" />
          <h2 className="text-2xl font-bold text-slate-800">Firmeninformationen</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <Label htmlFor="company-name">Firmenname</Label>
              <Input
                id="company-name"
                value={companyInfo.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Ihre Agentur GmbH"
              />
            </div>

            <div>
              <Label htmlFor="company-address">Adresse</Label>
              <Input
                id="company-address"
                value={companyInfo.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                placeholder="Musterstraße 123"
              />
            </div>

            <div>
              <Label htmlFor="company-city">PLZ & Ort</Label>
              <Input
                id="company-city"
                value={companyInfo.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                placeholder="12345 Musterstadt"
              />
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="company-phone">Telefon</Label>
              <Input
                id="company-phone"
                value={companyInfo.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                placeholder="+49 123 456789"
              />
            </div>

            <div>
              <Label htmlFor="company-email">E-Mail</Label>
              <Input
                id="company-email"
                type="email"
                value={companyInfo.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="info@ihre-agentur.de"
              />
            </div>

            <div>
              <Label htmlFor="company-website">Website</Label>
              <Input
                id="company-website"
                value={companyInfo.website}
                onChange={(e) => handleInputChange('website', e.target.value)}
                placeholder="www.ihre-agentur.de"
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Financial Information */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <CreditCard className="w-6 h-6 text-green-600" />
          <h3 className="text-xl font-semibold text-slate-800">Finanzinformationen</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <Label htmlFor="tax-id">Steuernummer / USt-IdNr.</Label>
              <Input
                id="tax-id"
                value={companyInfo.taxId || ''}
                onChange={(e) => handleInputChange('taxId', e.target.value)}
                placeholder="DE123456789"
              />
            </div>

            <div>
              <Label htmlFor="iban">IBAN</Label>
              <Input
                id="iban"
                value={companyInfo.iban || ''}
                onChange={(e) => handleInputChange('iban', e.target.value)}
                placeholder="DE89 3704 0044 0532 0130 00"
              />
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="bic">BIC</Label>
              <Input
                id="bic"
                value={companyInfo.bic || ''}
                onChange={(e) => handleInputChange('bic', e.target.value)}
                placeholder="COBADEFFXXX"
              />
            </div>

            <div>
              <Label htmlFor="bank-name">Bank Name</Label>
              <Input
                id="bank-name"
                value={companyInfo.bankName || ''}
                onChange={(e) => handleInputChange('bankName', e.target.value)}
                placeholder="Commerzbank AG"
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Legal Information */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <FileText className="w-6 h-6 text-purple-600" />
          <h3 className="text-xl font-semibold text-slate-800">Rechtliche Informationen</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label htmlFor="company-register">Handelsregisternummer</Label>
            <Input
              id="company-register"
              value={companyInfo.companyRegister || ''}
              onChange={(e) => handleInputChange('companyRegister', e.target.value)}
              placeholder="HRB 123456"
            />
          </div>

          <div>
            <Label htmlFor="ceo">Geschäftsführer</Label>
            <Input
              id="ceo"
              value={companyInfo.ceo || ''}
              onChange={(e) => handleInputChange('ceo', e.target.value)}
              placeholder="Max Mustermann"
            />
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <Palette className="w-6 h-6 text-blue-600" />
          <h3 className="text-xl font-semibold text-slate-800">Branding & Farben</h3>
        </div>

        {/* Layout-Stil (Struktur der Angebotsseite) */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <Layout className="w-4 h-4 text-blue-600" />
            <h4 className="text-md font-semibold text-slate-700">Layout-Stil der Angebotsseite</h4>
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" aria-label="Erklärung Layout-Stil" className="text-slate-400 hover:text-slate-600">
                    <Info className="w-4 h-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-xs leading-relaxed">
                  Der Layout-Stil bestimmt den <strong>Aufbau</strong> der Angebotsseite.
                  Bei den vier Standard-Stilen (Dokument, Pitch, Premium, Onepager) übernimmt das Layout
                  vollständig deine CI-Farben. Bei den markanten Stilen (Apple, Linear, Stripe, Notion,
                  Swiss, Brutalist) bleibt der typische Look erhalten — deine Markenfarbe greift nur als
                  Akzent (per Schalter unten ein-/ausschaltbar). Schrift &amp; Farben bleiben überall anpassbar.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <p className="text-sm text-slate-500 mb-4">Bestimmt den <strong>Aufbau</strong> der öffentlichen Angebotsseite. Farben &amp; Schrift kommen aus Ihrer CI (unten) und werden in jedes Layout übernommen.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            {QUOTE_LAYOUTS.map((layout) => {
              const isActive = getLayoutId(companyInfo) === layout.id;
              return (
                <button
                  key={layout.id}
                  type="button"
                  onClick={() => {
                    const updated = { ...companyInfo, landingPageLayout: layout.id };
                    setCompanyInfo(updated);
                    if (onCompanyUpdate) onCompanyUpdate(updated);
                  }}
                  className={`relative rounded-xl border-2 p-3 text-left transition-all hover:scale-105 ${
                    isActive ? 'border-blue-500 ring-2 ring-blue-200 bg-blue-50' : 'border-slate-200 hover:border-slate-400 bg-white'
                  }`}
                >
                  <div className="font-semibold text-sm text-slate-800">{layout.name}</div>
                  <div className="text-xs text-slate-500 mt-1 leading-snug">{layout.description}</div>
                  {isActive && (
                    <div className="absolute top-2 right-2 w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                      <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* CI-Akzent-Schalter — nur bei Signature-Stilen relevant */}
          {getLayoutById(getLayoutId(companyInfo)).mode === 'signature' && (() => {
            const useBrand = companyInfo.signatureBrandAccent !== false;
            return (
              <div className="mt-4 flex items-start justify-between gap-4 p-4 rounded-xl bg-slate-50 border border-slate-200">
                <div>
                  <p className="text-sm font-semibold text-slate-700">Markenfarbe als Akzent verwenden</p>
                  <p className="text-xs text-slate-500 mt-1">
                    {useBrand
                      ? 'An: Deine Markenfarbe greift an Buttons & Akzenten dieses Stils.'
                      : 'Aus: Der Stil nutzt seinen eigenen Original-Akzent (z.B. Swiss = Rot, Stripe = Indigo).'}
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={useBrand}
                  onClick={() => {
                    const updated = { ...companyInfo, signatureBrandAccent: !useBrand };
                    setCompanyInfo(updated);
                    if (onCompanyUpdate) onCompanyUpdate(updated);
                  }}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${useBrand ? 'bg-amber-500' : 'bg-slate-300'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${useBrand ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
            );
          })()}
        </div>

        {/* Landing Page Farbschema (Quick-Presets) */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <Palette className="w-4 h-4 text-blue-600" />
            <h4 className="text-md font-semibold text-slate-700">Farbschema (Schnellauswahl)</h4>
          </div>
          <p className="text-sm text-slate-500 mb-4">Optionale Farb-Presets. Überschreiben Ihre CI-Farben unten — die Sie jederzeit einzeln anpassen können.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {LANDING_PAGE_TEMPLATES.map((template) => {
              const isActive = (companyInfo.landingPageTemplate || 'modern') === template.id;
              return (
                <button
                  key={template.id}
                  type="button"
                  onClick={() => {
                    // Apply all template colors at once in a single state update
                    const updated = {
                      ...companyInfo,
                      landingPageTemplate: template.id,
                      ...template.colors
                    };
                    setCompanyInfo(updated);
                    if (onCompanyUpdate) onCompanyUpdate(updated);
                  }}
                  className={`relative rounded-xl border-2 p-3 text-left transition-all hover:scale-105 ${
                    isActive ? 'border-blue-500 ring-2 ring-blue-200' : 'border-slate-200 hover:border-slate-400'
                  }`}
                  style={{ background: template.preview.bg }}
                >
                  {/* Mini Preview */}
                  <div className="rounded-lg overflow-hidden mb-2" style={{ background: template.preview.bg, border: `1px solid ${template.preview.accent}33` }}>
                    <div className="h-5 rounded-t-lg flex items-center px-2 gap-1" style={{ background: template.preview.header }}>
                      <div className="w-2 h-2 rounded-full" style={{ background: template.preview.accent }}></div>
                      <div className="w-8 h-1 rounded" style={{ background: `${template.preview.accent}66` }}></div>
                    </div>
                    <div className="p-2 space-y-1">
                      <div className="h-1.5 rounded w-3/4" style={{ background: `${template.preview.text}33` }}></div>
                      <div className="h-1.5 rounded w-1/2" style={{ background: template.preview.accent }}></div>
                      <div className="h-1 rounded w-full" style={{ background: `${template.preview.text}22` }}></div>
                    </div>
                  </div>
                  <div className="font-semibold text-xs" style={{ color: template.preview.text === '#f1f5f9' ? '#ffffff' : template.preview.text }}>
                    {template.name}
                  </div>
                  <div className="text-xs mt-0.5 line-clamp-1" style={{ color: template.preview.text === '#f1f5f9' ? '#94a3b8' : '#64748b', fontSize: '9px' }}>
                    {template.borderRadius === '0px' ? 'Eckig' : template.borderRadius === '20px' || template.borderRadius === '16px' ? 'Rund' : 'Standard'}
                    {' · '}
                    {template.font?.includes('serif') && !template.font?.includes('sans') ? 'Serif' : template.font?.includes('Space') || template.font?.includes('DM') || template.font?.includes('Poppins') ? 'Modern' : 'Sans'}
                  </div>
                  {isActive && (
                    <div className="absolute top-2 right-2 w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                      <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-slate-400 mt-2">Die Auswahl überschreibt die manuellen Farbeinstellungen unten. Sie können diese danach noch anpassen.</p>
        </div>

        {/* Logo Upload */}
        <div className="mb-8">
          <Label htmlFor="logo-upload">Firmenlogo</Label>
          <div className="mt-2">
            <input
              id="logo-upload"
              type="file"
              accept="image/*"
              onChange={handleLogoUpload}
              className="hidden"
            />
            <div 
              className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors cursor-pointer"
              onClick={() => document.getElementById('logo-upload').click()}
            >
              {(logoPreview || companyInfo.logo) ? (
                <div className="space-y-3">
                  <img
                    src={logoPreview || companyInfo.logo}
                    alt="Logo Vorschau"
                    className="max-w-full max-h-24 mx-auto object-contain"
                  />
                  <p className="text-sm text-green-600">Logo hochgeladen ✅</p>
                  <Button type="button" variant="outline" size="sm">
                    <Camera className="w-4 h-4 mr-2" />
                    Logo ändern
                  </Button>
                </div>
              ) : (
                <>
                  <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                  <p className="text-sm text-slate-600">Logo hochladen</p>
                  <p className="text-xs text-slate-400 mt-1">PNG, JPG bis 2MB</p>
                  <Button type="button" variant="outline" size="sm" className="mt-3">
                    Datei auswählen
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* CI Colors */}
        <div className="mb-6">
          <h4 className="text-md font-semibold text-slate-700 mb-4">🎨 Corporate Identity Farben</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <Label htmlFor="primary-color">Primärfarbe</Label>
              <div className="mt-2 space-y-2">
                <Input
                  id="primary-color"
                  type="color"
                  value={companyInfo.primaryColor || '#3b82f6'}
                  onChange={(e) => handleInputChange('primaryColor', e.target.value)}
                  className="h-12 w-full cursor-pointer"
                />
                <Input
                  value={companyInfo.primaryColor || '#3b82f6'}
                  onChange={(e) => handleInputChange('primaryColor', e.target.value)}
                  placeholder="#3b82f6"
                  className="text-center"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="secondary-color">Sekundärfarbe</Label>
              <div className="mt-2 space-y-2">
                <Input
                  id="secondary-color"
                  type="color"
                  value={companyInfo.secondaryColor || '#8b5cf6'}
                  onChange={(e) => handleInputChange('secondaryColor', e.target.value)}
                  className="h-12 w-full cursor-pointer"
                />
                <Input
                  value={companyInfo.secondaryColor || '#8b5cf6'}
                  onChange={(e) => handleInputChange('secondaryColor', e.target.value)}
                  placeholder="#8b5cf6"
                  className="text-center"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="accent-color">Akzentfarbe</Label>
              <div className="mt-2 space-y-2">
                <Input
                  id="accent-color"
                  type="color"
                  value={companyInfo.accentColor || '#f59e0b'}
                  onChange={(e) => handleInputChange('accentColor', e.target.value)}
                  className="h-12 w-full cursor-pointer"
                />
                <Input
                  value={companyInfo.accentColor || '#f59e0b'}
                  onChange={(e) => handleInputChange('accentColor', e.target.value)}
                  placeholder="#f59e0b"
                  className="text-center"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Cost Type Colors */}
        <div>
          <h4 className="text-md font-semibold text-slate-700 mb-4">💰 Kostentyp-Farben</h4>
          <p className="text-sm text-slate-600 mb-4">
            Diese Farben werden in der Kostenübersicht und im PDF verwendet
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <Label htmlFor="color-einmalig">Einmalige Kosten</Label>
              <div className="mt-2 space-y-2">
                <Input
                  id="color-einmalig"
                  type="color"
                  value={companyInfo.colorEinmalig || '#22c55e'}
                  onChange={(e) => handleInputChange('colorEinmalig', e.target.value)}
                  className="h-12 w-full cursor-pointer"
                />
                <Input
                  value={companyInfo.colorEinmalig || '#22c55e'}
                  onChange={(e) => handleInputChange('colorEinmalig', e.target.value)}
                  placeholder="#22c55e"
                  className="text-center"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="color-monatlich">Monatliche Kosten</Label>
              <div className="mt-2 space-y-2">
                <Input
                  id="color-monatlich"
                  type="color"
                  value={companyInfo.colorMonatlich || '#f97316'}
                  onChange={(e) => handleInputChange('colorMonatlich', e.target.value)}
                  className="h-12 w-full cursor-pointer"
                />
                <Input
                  value={companyInfo.colorMonatlich || '#f97316'}
                  onChange={(e) => handleInputChange('colorMonatlich', e.target.value)}
                  placeholder="#f97316"
                  className="text-center"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="color-jaehrlich">Jährliche Kosten</Label>
              <div className="mt-2 space-y-2">
                <Input
                  id="color-jaehrlich"
                  type="color"
                  value={companyInfo.colorJaehrlich || '#a855f7'}
                  onChange={(e) => handleInputChange('colorJaehrlich', e.target.value)}
                  className="h-12 w-full cursor-pointer"
                />
                <Input
                  value={companyInfo.colorJaehrlich || '#a855f7'}
                  onChange={(e) => handleInputChange('colorJaehrlich', e.target.value)}
                  placeholder="#a855f7"
                  className="text-center"
                />
              </div>
            </div>
          </div>
        </div>
      </Card>
      
      {/* Landing Page Hintergrundfarbe - Separate Card */}
      <Card className="p-6 bg-blue-50 border-blue-200">
        <div className="flex items-center gap-3 mb-6">
          <Palette className="w-6 h-6 text-blue-600" />
          <h3 className="text-xl font-semibold text-slate-800">Landing Page Hintergrundfarbe</h3>
        </div>
        <p className="text-sm text-slate-600 mb-4">
          Diese Farbe wird nur auf der öffentlichen Landing Page für Kunden angezeigt. Im PDF bleibt der Hintergrund weiß.
        </p>
        <div>
          <Label htmlFor="landing-bg-color">Hintergrundfarbe</Label>
          <div className="mt-2 space-y-2">
            <Input
              id="landing-bg-color"
              type="color"
              value={companyInfo.landingPageBgColor || '#f1f5f9'}
              onChange={(e) => handleInputChange('landingPageBgColor', e.target.value)}
              className="h-16 w-full cursor-pointer"
            />
            <Input
              value={companyInfo.landingPageBgColor || '#f1f5f9'}
              onChange={(e) => handleInputChange('landingPageBgColor', e.target.value)}
              placeholder="#f1f5f9"
              className="text-center text-lg"
            />
          </div>
        </div>
      </Card>

      {/* AGB Section */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <Scale className="w-6 h-6 text-indigo-600" />
          <h3 className="text-xl font-semibold text-slate-800">Allgemeine Geschäftsbedingungen</h3>
        </div>

        <div className="space-y-4">
          <div>
            <Label htmlFor="agb-content">AGB-Text (wird automatisch an jedes PDF angehängt)</Label>
            <div className="mt-2">
              <RichTextEditor
                value={companyInfo.termsAndConditions || ''}
                onChange={(content) => handleInputChange('termsAndConditions', content)}
                placeholder="AGB-Text eingeben..."
                height={400}
              />
            </div>
            <p className="text-xs text-slate-500 mt-2">
              💡 Diese AGBs werden automatisch an jedes exportierte PDF angehängt.
            </p>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Target className="w-6 h-6 text-blue-600" />
            <div>
              <h3 className="text-lg font-semibold text-slate-800">Sales-Coaching-System</h3>
              <p className="text-sm text-slate-500">Zeigt Schritt 0 (Sales-Kompass) und Live-Coaching-Widget im Kalkulator</p>
            </div>
          </div>
          <Switch
            checked={companyInfo.salesCoachingEnabled === true}
            onCheckedChange={(checked) => handleInputChange('salesCoachingEnabled', checked)}
            data-testid="sales-coaching-toggle"
          />
        </div>
        {companyInfo.salesCoachingEnabled && (
          <div className="mt-2 p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
            Sales-Kompass ist aktiv. In Schritt 0 werden 5 Fragen zum Kunden-Kontext gestellt. Das Coaching-Widget erscheint dann im Kalkulator (Schritt 3).
          </div>
        )}
      </Card>

      <Card className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <DollarSign className="w-6 h-6 text-green-600" />
          <h3 className="text-lg font-semibold text-slate-800">Vertrieb & Provision</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label htmlFor="commission-rate">Provisionsrate (%)</Label>
            <div className="flex items-center gap-2 mt-2">
              <Input
                id="commission-rate"
                type="number"
                min="0"
                max="100"
                step="0.5"
                value={Math.round((companyInfo.commissionRate ?? 0.10) * 100)}
                onChange={(e) => handleInputChange('commissionRate', parseFloat(e.target.value) / 100)}
                className="w-32"
              />
              <span className="text-slate-600 text-sm">% vom Angebotswert</span>
            </div>
            <p className="text-xs text-slate-500 mt-1">Wird im Vertrieb-Dashboard angezeigt</p>
          </div>
          <div>
            <Label htmlFor="monthly-goal">Monatliches Umsatzziel (€)</Label>
            <div className="flex items-center gap-2 mt-2">
              <Input
                id="monthly-goal"
                type="number"
                min="0"
                step="1000"
                value={companyInfo.monthlyGoal ?? 50000}
                onChange={(e) => handleInputChange('monthlyGoal', parseFloat(e.target.value))}
                className="w-40"
              />
              <span className="text-slate-600 text-sm">€ / Monat</span>
            </div>
            <p className="text-xs text-slate-500 mt-1">Basis für den Fortschrittsbalken</p>
          </div>
        </div>
      </Card>

      <div className="flex justify-end mt-8">
        <Button 
          type="button"
          className={`transition-all duration-300 ${saveSuccess ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'}`}
          onClick={() => {
            console.log('🔍 uploadedLogoUrlRef.current:', uploadedLogoUrlRef.current);
            console.log('🔍 companyInfo FULL OBJECT:', JSON.stringify(companyInfo, null, 2));
            
            // Get final company info with uploaded logo URL from ref
            const finalCompanyInfo = {
              ...companyInfo,
              // FORCE logo from ref
              logo: uploadedLogoUrlRef.current || companyInfo.logo
            };
            
            console.log('🔍 finalCompanyInfo FULL OBJECT:', JSON.stringify(finalCompanyInfo, null, 2));
            
            // Ensure company info is updated before saving
            if (onCompanyUpdate) {
              onCompanyUpdate(finalCompanyInfo);
            }
            // Call onSave with final companyInfo including logo
            if (onSave) {
              onSave(finalCompanyInfo);
            } else {
              handleSaveSettings();
            }
          }}
          disabled={uploading || loading}
        >
          {saveSuccess ? (
            <>
              ✅ Gespeichert
            </>
          ) : (
            uploading || loading ? 'Speichere...' : (saveButtonText || 'Einstellungen speichern')
          )}
        </Button>
      </div>
    </div>
  );
};

export default CompanySettings;